/**
 * Shared "Recently compared" panel + its collapse-to-link disclosure, used by
 * BOTH compare setup dialogs (Schema Compare and Data Compare).
 *
 * Round-3 showcase review, R3-4 / R3-10: the recents list used to sit
 * permanently at the top of the Schema Compare setup dialog, pushing
 * SOURCE/TARGET down even after the user had already picked their endpoints.
 * It now collapses into a link button in the dialog header as soon as a
 * connection is chosen, and stays expanded before that — recents are the
 * fastest path when nothing is selected yet, and pure vertical noise once the
 * user has moved on to configuring the comparison by hand.
 *
 * The two dialogs share this file rather than each owning a near-identical
 * copy (they did, for the list; the review asked explicitly for one
 * interaction across both), so the disclosure cannot drift between them. Each
 * feature keeps a thin `RecentlyComparedSection` adapter that maps its own
 * store entries onto `RecentComparedItem` and fires its own analytics events;
 * everything visual and behavioural lives here.
 *
 * `idPrefix` preserves each feature's existing `data-testid` vocabulary
 * (`recent-schema-compare-*` / `recent-data-compare-*`) so the specs written
 * against those ids keep testing the same things.
 */
import { useMemo, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronRight, Clock, History, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/**
 * A recent comparison, flattened to what the panel actually paints. Feature
 * adapters own the mapping — Schema Compare's detail is a database name,
 * Data Compare's is `database · schema.table` — so the panel never has to
 * know which compare kind it is rendering.
 */
export type RecentComparedItem = {
  id: string;
  /** Connection alias / host, rendered bold. */
  sourceLabel: string;
  /** Whatever identifies the compared thing on that connection. */
  sourceDetail: string;
  targetLabel: string;
  targetDetail: string;
  /** Small badge under the row: compare scope, or key/value mapping counts. */
  badgeLabel: string;
  comparedAt: Date;
  /** False renders an inline "(connection unavailable)" warning; the entry stays selectable. */
  connectionExists: boolean;
  /** Pre-lowercased haystack for the "search earlier comparisons" box. */
  searchText: string;
};

export type RecentEntryPosition = 'top' | 'expanded';

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

type EntryRowProps = {
  item: RecentComparedItem;
  position: RecentEntryPosition;
  idPrefix: string;
  onSelect: (id: string, position: RecentEntryPosition) => void;
  onRemove: (id: string) => void;
};

function EntryRow({ item, position, idPrefix, onSelect, onRemove }: EntryRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id, position)}
      data-testid={`${idPrefix}-entry-${position}`}
      className="group flex w-full items-center gap-2 rounded-md border bg-card px-3 py-2 text-left text-sm transition hover:border-primary/40 hover:bg-accent/40"
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium">{item.sourceLabel}</span>
          <span className="truncate text-muted-foreground">/ {item.sourceDetail}</span>
          <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{item.targetLabel}</span>
          <span className="truncate text-muted-foreground">/ {item.targetDetail}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-normal">
            {item.badgeLabel}
          </Badge>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(item.comparedAt)}
          </span>
          {!item.connectionExists && (
            <span className="text-warning">(connection unavailable)</span>
          )}
        </div>
      </div>
      <span
        role="button"
        tabIndex={-1}
        aria-label="Remove from history"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        className="rounded p-1 text-muted-foreground opacity-0 transition hover:bg-muted hover:text-foreground group-hover:opacity-100"
        data-testid={`${idPrefix}-remove-${item.id}`}
      >
        <X className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

type RecentlyComparedLinkButtonProps = {
  open: boolean;
  onToggle: () => void;
  testId: string;
};

/**
 * The collapsed form of the panel — a link in the dialog header. Rendered by
 * the feature adapters only when there is at least one entry to show, so it
 * never advertises an empty list.
 */
export function RecentlyComparedLinkButton({
  open,
  onToggle,
  testId,
}: RecentlyComparedLinkButtonProps) {
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      aria-expanded={open}
      onClick={onToggle}
      data-testid={testId}
      className="h-auto gap-1.5 p-0 text-xs font-medium"
    >
      <History className="h-3.5 w-3.5" />
      Recently compared
    </Button>
  );
}

type RecentlyComparedPanelProps = {
  items: RecentComparedItem[];
  /** `data-testid` root for the section wrapper, kept per-feature. */
  sectionTestId: string;
  /** Prefix for entry/remove/toggle/search test ids, kept per-feature. */
  idPrefix: string;
  searchPlaceholder: string;
  onSelect: (id: string, position: RecentEntryPosition) => void;
  onRemove: (id: string) => void;
};

export function RecentlyComparedPanel({
  items,
  sectionTestId,
  idPrefix,
  searchPlaceholder,
  onSelect,
  onRemove,
}: RecentlyComparedPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOlder = useMemo(() => {
    if (items.length <= 1) return [];
    const query = search.toLowerCase();
    return items.slice(1).filter((item) => !query || item.searchText.includes(query));
  }, [items, search]);

  if (items.length === 0) return null;

  const top = items[0];
  const olderCount = items.length - 1;
  const showSearch = olderCount >= 2;

  return (
    <div className="space-y-2 border-b pb-3" data-testid={sectionTestId}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <History className="h-3.5 w-3.5" />
        Recently compared
      </div>

      <EntryRow
        item={top}
        position="top"
        idPrefix={idPrefix}
        onSelect={onSelect}
        onRemove={onRemove}
      />

      {olderCount > 0 && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`${idPrefix}-toggle-older`}
          >
            {expanded ? (
              <ChevronDown className="mr-1 h-3 w-3" />
            ) : (
              <ChevronRight className="mr-1 h-3 w-3" />
            )}
            {expanded ? 'Hide' : 'Show'} {olderCount} earlier comparison{olderCount === 1 ? '' : 's'}
          </Button>

          {expanded && (
            <div className="space-y-2">
              {showSearch && (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-8 pl-7 text-xs"
                    data-testid={`${idPrefix}-search`}
                  />
                </div>
              )}
              <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                {filteredOlder.length === 0 ? (
                  <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                    No matches.
                  </div>
                ) : (
                  filteredOlder.map((item) => (
                    <EntryRow
                      key={item.id}
                      item={item}
                      position="expanded"
                      idPrefix={idPrefix}
                      onSelect={onSelect}
                      onRemove={onRemove}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
